"use client";

import type { Locale } from "@i18n/react";

import { URL_SEARCH } from "../../../lib/constants/urls/urlSearch";
import { URL_SEARCH_SERVICE } from "../../../lib/constants/urls/urlSearchService";
import type { SearchResultSchema } from "../../../lib/types/schema/search/searchResultSchema";
import { searchResultSchema } from "../../../lib/types/schema/search/searchResultSchema";
import type { UseRequestInfiniteQueryProps } from "../../../request/hooks/useRequestInfiniteQuery";
import { useRequestInfiniteQuery } from "../../../request/hooks/useRequestInfiniteQuery";
import { useSession } from "../../../session/hooks/useSession";
import { useIsKidAccount } from "../me/useIsKidAccount";
import { ELASTIC_SEARCH_DIRECTORY } from "./constants/elasticSearchDirectory.constants";
import { PAGE_SIZE_SEARCH } from "./constants/pageSizeSearch.constants";
import { keySearch } from "./keySearch";
import type { SearchItemType } from "./types/enums/searchItemTypeEnum.enums";

export type UseRequestSearchProps = Partial<UseRequestInfiniteQueryProps> & {
  filters: {
    /**
     * Elastic `categories` values to exclude from results, emitted as a
     * `none` filter clause. Opt-in: omit it and the request body is unchanged.
     */
    excludedCategories?: string[];
    locale: Locale;
    type?: SearchItemType;
  };
  forceKidsLibrary?: boolean;
  maxPageCount?: number;
  search: string;
  /**
   * Route to Hallow's search service instead of Elastic App Search (the
   * `web_search_service` flag). Same request body; the service resolves
   * entitlement (`has_access`) from the user's JWT, or as a guest without one.
   */
  searchService?: boolean;
};

const searchAccessKey = (token?: string | null) => {
  if (!token) return "guest";
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const { id, sub } = JSON.parse(atob(payload));
    return `${id}:${sub ? 1 : 0}`;
  } catch {
    return "invalid";
  }
};

export const useRequestSearch = ({
  filters,
  forceKidsLibrary,
  maxPageCount,
  search,
  searchService = false,
  ...props
}: UseRequestSearchProps) => {
  const { isKidAccount } = useIsKidAccount();
  const { status, token } = useSession();
  const shouldFilterKidsLibrary = isKidAccount || forceKidsLibrary;

  return {
    infiniteQuery: useRequestInfiniteQuery({
      // The service's `has_access` depends on the JWT, so wait for a resolving
      // session's token rather than searching as a guest and caching that.
      enabledWithoutSessionToken:
        !searchService || status === "unauthenticated",
      getNextPageParam: (lastPage: SearchResultSchema) => {
        const { current, total_pages } = lastPage.meta.page;
        if (current >= total_pages) return null;
        if (maxPageCount && current >= maxPageCount) return null;
        return current + 1;
      },
      initialPageParam: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      queryFnRequestProps: ({ pageParam }) => ({
        body: JSON.stringify({
          filters: {
            all: [
              {
                languages: [filters.locale],
              },
              {
                type: filters.type
                  ? [filters.type]
                  : [
                      "page",
                      "collection",
                      "prayer",
                      "radio_station",
                      "setting",
                      "navigation",
                    ],
              },
              ...(shouldFilterKidsLibrary ? [{ in_kids_library: "true" }] : []),
            ],
            ...(filters.excludedCategories?.length
              ? { none: [{ categories: filters.excludedCategories }] }
              : {}),
          },
          page: {
            current: pageParam,
            size: PAGE_SIZE_SEARCH,
          },
          query: search.toLowerCase(),
        }),
        method: "POST",
        requestType: searchService ? "searchService" : "search",
        schema: searchResultSchema,
        url: searchService
          ? `${URL_SEARCH_SERVICE}/app/search`
          : `${URL_SEARCH}/${ELASTIC_SEARCH_DIRECTORY}/search`,
      }),
      queryKey: keySearch({
        accessKey: searchService ? searchAccessKey(token) : undefined,
        filters,
        isKidAccount: shouldFilterKidsLibrary,
        search,
        searchService,
      }),
      ...props,
    }),
  };
};
